"use client";

import {NeoDatePicker} from "@/components/forms";
import {NeoError} from "@/components/shared";
import {useNeoContext} from "@/context/neo/neo.context";
import {BusIcon, routesColor} from "@/data";
import {toKilometers} from "@/libs/helper";
import {liveService} from "@/service/live.service";
import {Spinner, Tooltip} from "@nextui-org/react";
import {
  GoogleMap,
  InfoWindow,
  Marker,
  Polyline,
  useJsApiLoader
} from "@react-google-maps/api";
import {useEffect, useMemo, useRef, useState} from "react";
import {
  FiAlertCircle,
  FiArrowDown,
  FiArrowUp,
  FiChevronLeft,
  FiChevronRight,
  FiInfo,
  FiList,
  FiMinus,
  FiPause,
  FiPlay,
  FiPlus,
  FiRefreshCw,
  FiSearch,
  FiTruck
} from "react-icons/fi";

type VehicleStatus = "Offline" | "Stationary" | "Moving" | "Alert" | "Idle";
type PlaybackSelectionMode = "multiVehicle" | "singleVehicle";
type PlaybackEvent =
  | "Normal"
  | "Stop"
  | "Overspeed"
  | "Harsh Brake"
  | "Acceleration"
  | "Idle";

type PlaybackEventCounts = {
  harshBraking: number;
  acceleration: number;
  overSpeed: number;
};

type PlaybackPoint = {
  lat: number;
  lng: number;
  timestamp: Date;
  speed: number;
  distanceKm: number;
  event: PlaybackEvent;
  address: string;
};

type PlaybackVehicle = {
  id: string;
  name: string;
  driver: string;
  group: string;
  routeName: string;
  color: string;
  status: VehicleStatus;
  distanceKm: number;
  runningTime: number;
  eventCounts: PlaybackEventCounts;
  route: PlaybackPoint[];
};

type PlaybackHistoryVehicle = {
  clientId?: string;
  name?: string;
  distance?: number | string;
  runningTime?: number | string;
  event?: Partial<PlaybackEventCounts>[];
};

type PlaybackHistoryPoint = {
  ti?: number | string;
  lat?: number | string;
  lon?: number | string;
  lng?: number | string;
  sp?: number | string;
  speed?: number | string;
  di?: number | string;
  distance?: number | string;
  distanceKm?: number | string;
  co?: string;
  address?: string;
  harshBraking?: number | string;
  acceleration?: number | string;
  overSpeed?: number | string;
};

type PlaybackFilters = {
  search: string;
  status: "All" | VehicleStatus;
  event: "All" | PlaybackEvent;
};
type VehicleSortKey = "vehicle" | "km" | "time";
type SortDirection = "asc" | "desc";

type VehicleRouteGroup = {
  vehicle: PlaybackVehicle;
  route: PlaybackPoint[];
};

type PlaybackRouteEntry = readonly [string, PlaybackPoint[]];

type PlaybackMarker = {
  vehicle: PlaybackVehicle;
  point: PlaybackPoint;
};

type PlaybackTableRow = PlaybackMarker & {
  id: string;
  pointIndex: number;
};

const mapKey: string = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || "";
const playbackSpeedConfig = [
  {
    speedFactor: 0.5,
    value: 2500
  },
  {
    speedFactor: 1,
    value: 1000
  },
  {
    speedFactor: 3,
    value: 200
  },
  {
    speedFactor: 5,
    value: 100
  },
  {
    speedFactor: 10,
    value: 50
  }
];
const playbackSpeeds = playbackSpeedConfig.map(item => item.speedFactor);
const playbackHistoryBatchSize = 5;
const showVehicleEventBadges = false;
const eventOptions: Array<"All" | PlaybackEvent> = [
  "All",
  "Normal",
  "Stop",
  "Overspeed",
  "Harsh Brake",
  "Acceleration",
  "Idle"
];

const mapStyles = [
  {
    featureType: "all",
    elementType: "labels.text.fill",
    stylers: [{color: "#7c93a3"}, {lightness: "-10"}]
  },
  {
    featureType: "administrative.country",
    elementType: "geometry.stroke",
    stylers: [{color: "#c2d1d6"}]
  },
  {
    featureType: "landscape",
    elementType: "geometry.fill",
    stylers: [{color: "#dde3e3"}]
  },
  {
    featureType: "road.highway",
    elementType: "geometry.fill",
    stylers: [{color: "#c2d1d6"}]
  },
  {
    featureType: "road.highway",
    elementType: "geometry.stroke",
    stylers: [{color: "#a9b4b8"}, {lightness: "0"}]
  },
  {
    featureType: "water",
    elementType: "geometry.fill",
    stylers: [{color: "#a3c7df"}]
  }
];

const eventStyles: Record<PlaybackEvent, string> = {
  Normal: "bg-[#eef4ff] text-[#4166f5]",
  Stop: "bg-[#f2f4f7] text-[#667085]",
  Overspeed: "bg-[#fff0f2] text-[#f04455]",
  "Harsh Brake": "bg-[#fff7e8] text-[#b75c00]",
  Acceleration: "bg-[#fff8db] text-[#946200]",
  Idle: "bg-[#edf8f3] text-[#008f75]"
};

// const playbackVehicleColors = [
//   "#ff9f1c",
//   "#16c6ae",
//   "#4f6df5",
//   "#ff4b5f",
//   "#7255d9",
//   "#1b9aaa",
//   "#2f80ed",
//   "#98a2b3"
// ];

const emptyEventCounts: PlaybackEventCounts = {
  harshBraking: 0,
  acceleration: 0,
  overSpeed: 0
};

const toNumber = (value: unknown) => {
  const numberValue =
    typeof value === "string"
      ? Number(value.replace(/,/g, "").match(/-?\d+(\.\d+)?/)?.[0])
      : Number(value);
  return Number.isFinite(numberValue) ? numberValue : 0;
};

const toPlaybackKilometers = (value: unknown) => {
  if (typeof value === "string" && value.toLowerCase().includes("km")) {
    return toNumber(value);
  }
  return toKilometers(toNumber(value));
};

const normalizeEventCounts = (
  events: PlaybackHistoryVehicle["event"]
): PlaybackEventCounts => {
  return (events ?? []).reduce<PlaybackEventCounts>(
    (counts, event) => ({
      harshBraking: counts.harshBraking + toNumber(event?.harshBraking ?? 0),
      acceleration: counts.acceleration + toNumber(event?.acceleration ?? 0),
      overSpeed: counts.overSpeed + toNumber(event?.overSpeed ?? 0)
    }),
    {...emptyEventCounts}
  );
};

const getSummaryEventCount = (
  vehicle: PlaybackVehicle,
  event: PlaybackEvent
) => {
  if (event === "Harsh Brake") return vehicle.eventCounts.harshBraking;
  if (event === "Acceleration") return vehicle.eventCounts.acceleration;
  if (event === "Overspeed") return vehicle.eventCounts.overSpeed;
  return 0;
};

const hasVehicleEvent = (
  vehicle: PlaybackVehicle,
  event: PlaybackEvent,
  route: PlaybackPoint[]
) =>
  route.some(point => point.event === event) ||
  getSummaryEventCount(vehicle, event) > 0;

const getVehicleStatus = (
  distanceKm: number,
  runningTime: number,
  eventCounts: PlaybackEventCounts
): VehicleStatus => {
  const totalEvents =
    eventCounts.harshBraking + eventCounts.acceleration + eventCounts.overSpeed;

  if (totalEvents > 0) return "Alert";
  if (runningTime > 0) return "Moving";
  if (distanceKm > 0) return "Stationary";
  return "Idle";
};

const mapPlaybackHistoryVehicles = (
  reportData: PlaybackHistoryVehicle[]
): PlaybackVehicle[] => {
  return reportData.map((vehicle, index) => {
    const distanceKm = toPlaybackKilometers(vehicle.distance);
    const runningTime = toNumber(vehicle.runningTime);
    const eventCounts = normalizeEventCounts(vehicle.event);

    return {
      id: vehicle.clientId || `vehicle-${index}`,
      name: vehicle.name || "Unknown vehicle",
      driver: "",
      group: "Playback history",
      routeName: "Playback summary",
      color: routesColor[index % routesColor.length],
      status: getVehicleStatus(distanceKm, runningTime, eventCounts),
      distanceKm,
      runningTime,
      eventCounts,
      route: []
    };
  });
};

const getPlaybackPointEvent = (
  point: PlaybackHistoryPoint,
  speed: number
): PlaybackEvent => {
  if (toNumber(point.overSpeed) > 0) return "Overspeed";
  if (toNumber(point.harshBraking) > 0) return "Harsh Brake";
  if (toNumber(point.acceleration) > 0) return "Acceleration";
  if (speed <= 0) return "Stop";
  return "Normal";
};

const mapPlaybackRoute = (history: PlaybackHistoryPoint[]): PlaybackPoint[] => {
  let totalDistanceKm = 0;

  return history
    .map(point => {
      const lat = toNumber(point.lat);
      const lng = toNumber(point.lon ?? point.lng);
      const timestampValue = toNumber(point.ti);
      const speed = toNumber(point.sp ?? point.speed);
      const distanceDeltaKm = toNumber(point.di) / 1000;

      if (!lat || !lng || !timestampValue) return null;

      totalDistanceKm += distanceDeltaKm;

      return {
        lat,
        lng,
        timestamp: new Date(timestampValue),
        speed,
        distanceKm:
          totalDistanceKm > 0
            ? Number(totalDistanceKm.toFixed(2))
            : toNumber(point.distanceKm ?? point.distance),
        event: getPlaybackPointEvent(point, speed),
        address: point.co || point.address || "Playback point"
      };
    })
    .filter((point): point is PlaybackPoint => Boolean(point));
};

const fetchPlaybackRoute = async (
  vehicle: PlaybackVehicle,
  rangeFrom: Date,
  rangeTo: Date
): Promise<PlaybackRouteEntry> => {
  try {
    const response = await liveService.onGetPlaybackHistory({
      clientId: vehicle.id,
      fromDate: rangeFrom.getTime(),
      toDate: rangeTo.getTime()
    });
    const playbackData = Array.isArray(response?.playbackData)
      ? response.playbackData
      : [];

    return [vehicle.id, mapPlaybackRoute(playbackData)] as const;
  } catch {
    return [vehicle.id, []] as const;
  }
};

const fetchPlaybackRoutesInBatches = async (
  vehicles: PlaybackVehicle[],
  rangeFrom: Date,
  rangeTo: Date,
  shouldContinue: () => boolean
) => {
  const routeEntries: PlaybackRouteEntry[] = [];

  for (
    let index = 0;
    index < vehicles.length;
    index += playbackHistoryBatchSize
  ) {
    if (!shouldContinue()) break;

    const batch = vehicles.slice(index, index + playbackHistoryBatchSize);
    const batchEntries = await Promise.all(
      batch.map(vehicle => fetchPlaybackRoute(vehicle, rangeFrom, rangeTo))
    );

    routeEntries.push(...batchEntries);
  }

  return routeEntries;
};

const getRouteInRange = (
  vehicle: PlaybackVehicle,
  range: DateRange
): PlaybackPoint[] => {
  return vehicle.route.filter(point => {
    const fromTime = range.from?.getTime();
    const toTime = range.to?.getTime();
    const pointTime = point.timestamp.getTime();

    return (
      (fromTime === undefined || fromTime === null || pointTime >= fromTime) &&
      (toTime === undefined || toTime === null || pointTime <= toTime)
    );
  });
};

const getRouteWindow = (route: PlaybackPoint[]) => {
  if (!route.length) return "No data";
  return `${formatTime(route[0].timestamp)} - ${formatTime(route[route.length - 1].timestamp)}`;
};

const getVehicleDistanceValue = (vehicle: PlaybackVehicle) =>
  vehicle.distanceKm;

const getVehicleTimeValue = (vehicle: PlaybackVehicle) => vehicle.runningTime;

const getPlaybackSidebarMetrics = (
  vehicle: PlaybackVehicle,
  dateRange: DateRange,
  currentPlaybackIndex: number
) => {
  const route = getRouteInRange(vehicle, dateRange);
  const currentPoint =
    route[Math.min(currentPlaybackIndex, route.length - 1)] ?? route[0];
  const firstPoint = route[0];

  if (!currentPoint || !firstPoint) return null;

  return {
    distanceKm: currentPoint.distanceKm,
    timestamp: currentPoint.timestamp,
    speed: currentPoint.speed
  };
};

const getRouteRenderKey = (group: VehicleRouteGroup) => {
  const firstPointTime = group.route[0]?.timestamp.getTime() ?? 0;
  const lastPointTime =
    group.route[group.route.length - 1]?.timestamp.getTime() ?? 0;

  return `${group.vehicle.id}-${firstPointTime}-${lastPointTime}-${group.route.length}`;
};

const formatDistance = (distanceKm: number) => `${distanceKm.toFixed(1)} km`;

const formatDuration = (milliseconds: number) => {
  const totalSeconds = Math.max(0, Math.round(milliseconds / 1000));

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;

  return `${seconds}s`;
};

const defaultPlaybackFromTime = "00:00";
const defaultPlaybackEndTime = "23:59";

const getPlaybackStartDate = () => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date;
};

const normalizePlaybackDate = (date: Date) => {
  const nextDate = new Date(date);
  nextDate.setHours(0, 0, 0, 0);
  return nextDate;
};

const normalizeEndTimeValue = (fromTime: string, endTime: string) => {
  if (!endTime) return fromTime;
  return endTime < fromTime ? fromTime : endTime;
};

const parseTimeInputValue = (value: string) => {
  const [hours, minutes] = value.split(":").map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  return {hours, minutes};
};

const applyTimeToDate = (date: Date, timeValue: string, seconds = 0) => {
  const nextDate = new Date(date);
  const parsedTime = parseTimeInputValue(timeValue);
  if (parsedTime) {
    nextDate.setHours(parsedTime.hours, parsedTime.minutes, seconds, 0);
  }
  return nextDate;
};

const getPlaybackDateTimeRange = (
  selectedDate: Date,
  selectedEndDate: Date,
  fromTime: string,
  endTime: string
): DateRange => {
  const from = applyTimeToDate(selectedDate, fromTime, 0);

  const to = applyTimeToDate(selectedEndDate, endTime, 59);
  if (to.getTime() < from.getTime()) {
    to.setTime(from.getTime());
  }

  return {from, to};
};

const formatTime = (date: Date) =>
  new Intl.DateTimeFormat("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true
  }).format(date);

const formatDateTime = (date: Date) =>
  new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true
  }).format(date);

const getPlaybackTimelineSummary = (markers: PlaybackMarker[]) => {
  if (!markers.length) return "No selected playback points";

  const sortedMarkers = [...markers].sort(
    (a, b) => a.point.timestamp.getTime() - b.point.timestamp.getTime()
  );
  const firstPoint = sortedMarkers[0].point;
  const lastPoint = sortedMarkers[sortedMarkers.length - 1].point;

  if (sortedMarkers.length === 1) {
    return `${formatDateTime(firstPoint.timestamp)}`;
  }

  const timeLabel =
    firstPoint.timestamp.getTime() === lastPoint.timestamp.getTime()
      ? formatDateTime(firstPoint.timestamp)
      : `${formatDateTime(firstPoint.timestamp)} to ${formatDateTime(lastPoint.timestamp)}`;

  return `${timeLabel} - ${sortedMarkers.length} vehicles`;
};

type LivePlaybackProps = {
  pageTabs?: React.ReactNode;
};

export const LivePlayback = ({pageTabs}: LivePlaybackProps = {}) => {
  const [selectionMode, setSelectionMode] =
    useState<PlaybackSelectionMode>("singleVehicle");
  const [selectedDate, setSelectedDate] = useState(getPlaybackStartDate);
  const [selectedEndDate, setSelectedEndDate] = useState(getPlaybackStartDate);
  const [selectedFromTime, setSelectedFromTime] = useState(
    defaultPlaybackFromTime
  );
  const [selectedEndTime, setSelectedEndTime] = useState(
    defaultPlaybackEndTime
  );
  const [playbackVehicles, setPlaybackVehicles] = useState<PlaybackVehicle[]>(
    []
  );
  const [playbackRoutes, setPlaybackRoutes] = useState<
    Record<string, PlaybackPoint[]>
  >({});
  const routeCacheRef = useRef<Record<string, PlaybackPoint[]>>({});
  const [isVehicleHistoryLoading, setIsVehicleHistoryLoading] = useState(false);
  const [isRouteHistoryLoading, setIsRouteHistoryLoading] = useState(false);
  const [vehicleHistoryError, setVehicleHistoryError] = useState("");
  const [filters, setFilters] = useState<PlaybackFilters>({
    search: "",
    status: "All",
    event: "All"
  });
  const [selectedVehicleIds, setSelectedVehicleIds] = useState<Set<string>>(
    () => new Set()
  );
  const [isPlaying, setIsPlaying] = useState(false);
  const [hoveredVehicleId, setHoveredVehicleId] = useState<string | null>(null);
  const [playSpeed, setPlaySpeed] = useState(3);
  const [currentPlaybackIndex, setCurrentPlaybackIndex] = useState(0);
  const [tablePage, setTablePage] = useState(1);
  const dateRange = useMemo(
    () =>
      getPlaybackDateTimeRange(
        selectedDate,
        selectionMode === "multiVehicle" ? selectedDate : selectedEndDate,
        selectedFromTime,
        selectedEndTime
      ),
    [
      selectedDate,
      selectedEndDate,
      selectedEndTime,
      selectedFromTime,
      selectionMode
    ]
  );

  useEffect(() => {
    const rangeFrom = dateRange.from;
    const rangeTo = dateRange.to;
    if (!rangeFrom || !rangeTo) return;

    let isCurrentRequest = true;
    const fetchPlaybackHistory = async () => {
      setIsVehicleHistoryLoading(true);
      setIsRouteHistoryLoading(false);
      setVehicleHistoryError("");
      routeCacheRef.current = {};
      setPlaybackRoutes({});
      setSelectedVehicleIds(new Set());
      setCurrentPlaybackIndex(0);
      setIsPlaying(false);

      const response = await liveService.onGetPlaybackSidebarHistory({
        from: rangeFrom.getTime(),
        to: rangeTo.getTime()
      });

      if (!isCurrentRequest) return;

      const nextVehicles = mapPlaybackHistoryVehicles(
        response.reportData ?? []
      );
      if (response.error) {
        setVehicleHistoryError("Unable to load playback history");
      }
      setPlaybackVehicles(nextVehicles);
      setPlaybackRoutes({});
      setSelectedVehicleIds(
        new Set(
          selectionMode === "multiVehicle"
            ? nextVehicles.map(vehicle => vehicle.id)
            : nextVehicles[0]?.id
              ? [nextVehicles[0].id]
              : []
        )
      );
      setCurrentPlaybackIndex(0);
      setIsPlaying(false);
      setIsVehicleHistoryLoading(false);
    };

    fetchPlaybackHistory().catch(() => {
      if (!isCurrentRequest) return;
      routeCacheRef.current = {};
      setPlaybackVehicles([]);
      setPlaybackRoutes({});
      setSelectedVehicleIds(new Set());
      setVehicleHistoryError("Unable to load playback history");
      setIsVehicleHistoryLoading(false);
    });

    return () => {
      isCurrentRequest = false;
    };
  }, [dateRange, selectionMode]);

  useEffect(() => {
    const rangeFrom = dateRange.from;
    const rangeTo = dateRange.to;
    if (!rangeFrom || !rangeTo) return;

    const selectedVehicles = playbackVehicles.filter(vehicle =>
      selectedVehicleIds.has(vehicle.id)
    );

    if (!selectedVehicles.length) {
      setIsRouteHistoryLoading(false);
      return;
    }

    // Only fetch vehicles whose routes are not yet cached
    const uncachedVehicles = selectedVehicles.filter(
      vehicle => !(vehicle.id in routeCacheRef.current)
    );

    // All selected vehicles already cached — update state directly, no API call
    if (!uncachedVehicles.length) {
      setPlaybackRoutes({...routeCacheRef.current});
      setIsRouteHistoryLoading(false);
      return;
    }

    let isCurrentRequest = true;

    const fetchSelectedVehicleRoutes = async () => {
      setIsRouteHistoryLoading(true);
      setCurrentPlaybackIndex(0);
      setIsPlaying(false);

      const routeEntries = await fetchPlaybackRoutesInBatches(
        uncachedVehicles,
        rangeFrom,
        rangeTo,
        () => isCurrentRequest
      );

      if (!isCurrentRequest) return;

      // Merge newly fetched routes into the persistent cache
      routeEntries.forEach(([vehicleId, route]) => {
        routeCacheRef.current[vehicleId] = route;
      });

      setPlaybackRoutes({...routeCacheRef.current});
      setCurrentPlaybackIndex(0);
      setIsPlaying(false);
      setIsRouteHistoryLoading(false);
    };

    fetchSelectedVehicleRoutes().catch(() => {
      if (!isCurrentRequest) return;
      setIsRouteHistoryLoading(false);
    });

    return () => {
      isCurrentRequest = false;
    };
  }, [dateRange, playbackVehicles, selectedVehicleIds]);

  const vehiclesWithRoutes = useMemo(() => {
    return playbackVehicles.map(vehicle => ({
      ...vehicle,
      route: playbackRoutes[vehicle.id] ?? []
    }));
  }, [playbackRoutes, playbackVehicles]);

  const filteredVehicles = useMemo(() => {
    const search = filters.search.trim().toLowerCase();

    return vehiclesWithRoutes.filter(vehicle => {
      const route = getRouteInRange(vehicle, dateRange);
      const matchesSearch =
        !search || vehicle.name.toLowerCase().includes(search);
      const matchesStatus =
        filters.status === "All" || vehicle.status === filters.status;
      const matchesEvent =
        filters.event === "All" ||
        hasVehicleEvent(vehicle, filters.event, route);

      return matchesSearch && matchesStatus && matchesEvent;
    });
  }, [dateRange, filters, vehiclesWithRoutes]);

  const activeRouteGroups = useMemo<VehicleRouteGroup[]>(() => {
    return filteredVehicles
      .filter(vehicle => selectedVehicleIds.has(vehicle.id))
      .map(vehicle => ({
        vehicle,
        route: getRouteInRange(vehicle, dateRange)
      }))
      .filter(group => group.route.length > 0);
  }, [dateRange, filteredVehicles, selectedVehicleIds]);

  const selectedFilteredVehicles = useMemo(() => {
    return filteredVehicles.filter(vehicle =>
      selectedVehicleIds.has(vehicle.id)
    );
  }, [filteredVehicles, selectedVehicleIds]);

  const maxPlaybackIndex = useMemo(() => {
    return activeRouteGroups.reduce(
      (max, group) => Math.max(max, group.route.length - 1),
      0
    );
  }, [activeRouteGroups]);

  const currentMarkers = useMemo<PlaybackMarker[]>(() => {
    return activeRouteGroups.map(group => ({
      vehicle: group.vehicle,
      point:
        group.route[Math.min(currentPlaybackIndex, group.route.length - 1)] ??
        group.route[0]
    }));
  }, [activeRouteGroups, currentPlaybackIndex]);

  const tableRows = useMemo<PlaybackTableRow[]>(() => {
    return activeRouteGroups
      .flatMap(group =>
        group.route
          .map((point, pointIndex) => ({
            id: `${group.vehicle.id}-${point.timestamp.getTime()}`,
            vehicle: group.vehicle,
            point,
            pointIndex
          }))
          .filter(
            row => filters.event === "All" || row.point.event === filters.event
          )
      )
      .sort(
        (a, b) => a.point.timestamp.getTime() - b.point.timestamp.getTime()
      );
  }, [activeRouteGroups, filters.event]);

  const eventCounts = useMemo(() => {
    const summaryEvents: PlaybackEvent[] = [
      "Acceleration",
      "Harsh Brake",
      "Overspeed"
    ];
    const summaryCounts = summaryEvents.map(event => ({
      label: event,
      count: selectedFilteredVehicles.reduce(
        (count, vehicle) => count + getSummaryEventCount(vehicle, event),
        0
      )
    }));

    if (selectedFilteredVehicles.length > 0) return summaryCounts;

    return eventOptions
      .filter((event): event is PlaybackEvent => event !== "All")
      .map(event => ({
        label: event,
        count: tableRows.filter(row => row.point.event === event).length
      }))
      .filter(event => event.label !== "Normal");
  }, [selectedFilteredVehicles, tableRows]);

  const totalTablePages = Math.max(1, Math.ceil(tableRows.length / 8));
  const pagedRows = tableRows.slice((tablePage - 1) * 8, tablePage * 8);
  const playbackRecordCount =
    tableRows.length || selectedFilteredVehicles.length;
  const allFilteredSelected =
    filteredVehicles.length > 0 &&
    filteredVehicles.every(vehicle => selectedVehicleIds.has(vehicle.id));
  const snapshotTime = currentMarkers[0]?.point.timestamp
    ? formatDateTime(currentMarkers[0].point.timestamp)
    : "No playback data";

  useEffect(() => {
    if (!isPlaying || maxPlaybackIndex === 0) return;

    const interval =
      playbackSpeedConfig.find(config => config.speedFactor === playSpeed)
        ?.value ?? 1000;
    const playbackInterval = window.setInterval(() => {
      setCurrentPlaybackIndex(index => Math.min(index + 1, maxPlaybackIndex));
    }, interval);

    return () => window.clearInterval(playbackInterval);
  }, [isPlaying, maxPlaybackIndex, playSpeed]);

  useEffect(() => {
    if (isPlaying && currentPlaybackIndex >= maxPlaybackIndex) {
      setIsPlaying(false);
    }
  }, [currentPlaybackIndex, isPlaying, maxPlaybackIndex]);

  useEffect(() => {
    setCurrentPlaybackIndex(index => Math.min(index, maxPlaybackIndex));
    if (maxPlaybackIndex === 0) {
      setIsPlaying(false);
    }
  }, [maxPlaybackIndex]);

  useEffect(() => {
    setTablePage(1);
  }, [dateRange, filters, selectedVehicleIds]);

  useEffect(() => {
    setTablePage(page => Math.min(page, totalTablePages));
  }, [totalTablePages]);

  const handleDateRangeApply = (
    range: DateRange,
    fromTime: string,
    endTime: string
  ) => {
    const nextFromTime = fromTime || defaultPlaybackFromTime;
    const nextFromDate = normalizePlaybackDate(range.from ?? selectedDate);
    const nextEndDate = normalizePlaybackDate(range.to ?? nextFromDate);
    setSelectedDate(nextFromDate);
    setSelectedEndDate(
      selectionMode === "multiVehicle" ? nextFromDate : nextEndDate
    );
    setSelectedFromTime(nextFromTime);
    setSelectedEndTime(normalizeEndTimeValue(nextFromTime, endTime));
    setCurrentPlaybackIndex(0);
    setIsPlaying(false);
  };

  const handleSelectionModeChange = (nextMode: PlaybackSelectionMode) => {
    setSelectionMode(nextMode);
    setCurrentPlaybackIndex(0);
    setIsPlaying(false);

    if (nextMode === "multiVehicle") {
      setSelectedEndDate(selectedDate);
      setSelectedVehicleIds(
        new Set(playbackVehicles.map(vehicle => vehicle.id))
      );
      return;
    }

    setSelectedVehicleIds(current => {
      const selectedVehicle =
        playbackVehicles.find(vehicle => current.has(vehicle.id)) ??
        playbackVehicles[0];

      return new Set(selectedVehicle ? [selectedVehicle.id] : []);
    });
  };

  const updateFilter = <TKey extends keyof PlaybackFilters>(
    key: TKey,
    value: PlaybackFilters[TKey]
  ) => {
    setFilters(current => ({...current, [key]: value}));
  };

  const toggleVehicle = (vehicleId: string) => {
    if (selectionMode === "singleVehicle") {
      setSelectedVehicleIds(new Set([vehicleId]));
      setCurrentPlaybackIndex(0);
      setIsPlaying(false);
      return;
    }

    setSelectedVehicleIds(current => {
      const next = new Set(current);
      if (next.has(vehicleId)) {
        next.delete(vehicleId);
      } else {
        next.add(vehicleId);
      }
      return next;
    });
  };

  const toggleFilteredVehicles = () => {
    if (selectionMode === "singleVehicle") {
      setSelectedVehicleIds(
        new Set(filteredVehicles[0]?.id ? [filteredVehicles[0].id] : [])
      );
      setCurrentPlaybackIndex(0);
      setIsPlaying(false);
      return;
    }

    setSelectedVehicleIds(current => {
      const next = new Set(current);

      if (allFilteredSelected) {
        filteredVehicles.forEach(vehicle => next.delete(vehicle.id));
      } else {
        filteredVehicles.forEach(vehicle => next.add(vehicle.id));
      }

      return next;
    });
  };

  const changeSpeed = (direction: "up" | "down") => {
    setPlaySpeed(current => {
      const currentIndex = playbackSpeeds.indexOf(current);
      const nextIndex =
        direction === "up"
          ? Math.min(currentIndex + 1, playbackSpeeds.length - 1)
          : Math.max(currentIndex - 1, 0);

      return playbackSpeeds[nextIndex] ?? current;
    });
  };

  const resetPlayback = () => {
    setCurrentPlaybackIndex(0);
    setIsPlaying(false);
  };

  const togglePlayback = () => {
    if (!isPlaying && currentPlaybackIndex >= maxPlaybackIndex) {
      setCurrentPlaybackIndex(0);
    }

    setIsPlaying(playing => !playing);
  };

  const jumpToTableRow = (row: PlaybackTableRow) => {
    setCurrentPlaybackIndex(row.pointIndex);
    setIsPlaying(false);
  };

  return (
    <main className="min-h-[calc(100vh-57px)] overflow-hidden bg-[#f6f8fc] text-[#23283b]">
      <div className="grid h-[calc(100vh-57px)] min-h-0 grid-rows-[86px_minmax(0,1fr)]">
        <PlaybackToolbar
          activeVehicleCount={selectedFilteredVehicles.length}
          onDateRangeApply={handleDateRangeApply}
          onSelectionModeChange={handleSelectionModeChange}
          pageTabs={pageTabs}
          selectedDate={selectedDate}
          selectedEndDate={selectedEndDate}
          selectedEndTime={selectedEndTime}
          selectedFromTime={selectedFromTime}
          selectionMode={selectionMode}
          snapshotTime={snapshotTime}
        />
        <section className="grid min-h-0 grid-cols-[minmax(360px,25%)_minmax(560px,1fr)] overflow-hidden border-t border-[#e8edf5]">
          <VehiclePanel
            allFilteredSelected={allFilteredSelected}
            currentPlaybackIndex={currentPlaybackIndex}
            dateRange={dateRange}
            filters={filters}
            isLoading={isVehicleHistoryLoading}
            errorMessage={vehicleHistoryError}
            canSelectMultipleVehicles={selectionMode === "multiVehicle"}
            onFilterChange={updateFilter}
            onToggleAll={toggleFilteredVehicles}
            onToggleVehicle={toggleVehicle}
            onVehicleHover={setHoveredVehicleId}
            selectedVehicleIds={selectedVehicleIds}
            vehicles={filteredVehicles}
          />
          <MapPlaybackCanvas
            activeRouteGroups={activeRouteGroups}
            currentMarkers={currentMarkers}
            currentPlaybackIndex={currentPlaybackIndex}
            hoveredVehicleId={hoveredVehicleId}
            isPlaying={isPlaying}
            isRouteHistoryLoading={isRouteHistoryLoading}
            isSingleVehicle={selectionMode === "singleVehicle"}
            maxPlaybackIndex={maxPlaybackIndex}
            onReset={resetPlayback}
            onSeek={setCurrentPlaybackIndex}
            onSpeedDown={() => changeSpeed("down")}
            onSpeedUp={() => changeSpeed("up")}
            onTogglePlay={togglePlayback}
            playSpeed={playSpeed}
            selectedVehicleCount={selectedFilteredVehicles.length}
          />
          {/* <RightStack
            currentPlaybackIndex={currentPlaybackIndex}
            eventCounts={eventCounts}
            onPageChange={setTablePage}
            onRowClick={jumpToTableRow}
            page={tablePage}
            pagedRows={pagedRows}
            recordCount={playbackRecordCount}
            tableRows={tableRows}
            totalPages={totalTablePages}
          /> */}
        </section>
      </div>
    </main>
  );
};

const PlaybackToolbar = ({
  activeVehicleCount,
  onDateRangeApply,
  onSelectionModeChange,
  pageTabs,
  selectedDate,
  selectedEndDate,
  selectedEndTime,
  selectedFromTime,
  selectionMode,
  snapshotTime
}: {
  activeVehicleCount: number;
  onDateRangeApply: (
    range: DateRange,
    fromTime: string,
    endTime: string
  ) => void;
  onSelectionModeChange: (mode: PlaybackSelectionMode) => void;
  pageTabs?: React.ReactNode;
  selectedDate: Date;
  selectedEndDate: Date;
  selectedEndTime: string;
  selectedFromTime: string;
  selectionMode: PlaybackSelectionMode;
  snapshotTime: string;
}) => {
  return (
    <header className="grid grid-cols-[minmax(360px,25%)_minmax(0,1fr)] border-b border-[#e8edf5] bg-white">
      <div className="flex items-center border-r border-[#e8edf5] px-4">
        {pageTabs ?? (
          <div className="flex flex-col justify-center">
            <span className="text-xs font-semibold uppercase tracking-wide text-[#8390a5]">
              Playback
            </span>
            <span className="mt-1 text-lg font-bold text-[#283047]">
              Fleet History
            </span>
          </div>
        )}
      </div>

      <div className="flex items-center justify-center px-5">
        <div className="w-full">
          <SinglePlaybackDatePicker
            onDateRangeApply={onDateRangeApply}
            onSelectionModeChange={onSelectionModeChange}
            selectedDate={selectedDate}
            selectedEndDate={selectedEndDate}
            selectedEndTime={selectedEndTime}
            selectedFromTime={selectedFromTime}
            selectionMode={selectionMode}
          />
        </div>
      </div>

      {/* <div className="grid grid-cols-2 border-l border-[#e8edf5]">
        <ToolbarMetric
          icon={<FiTruck />}
          label="Vehicles"
          value={activeVehicleCount.toString()}
        />
        <ToolbarMetric
          icon={<FiClock />}
          label="Snapshot"
          value={snapshotTime}
        />
      </div> */}
    </header>
  );
};

const ToolbarMetric = ({
  icon,
  label,
  value
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) => {
  return (
    <div className="flex min-w-0 items-center gap-3 border-r border-[#eef2f7] px-4">
      <span className="flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-[#f3f5fb] text-[#5b3fe4] [&_svg]:h-5 [&_svg]:w-5">
        {icon}
      </span>
      <div className="min-w-0">
        <p className="text-xs font-semibold text-[#8390a5]">{label}</p>
        <p className="truncate text-sm font-bold text-[#30384e]">{value}</p>
      </div>
    </div>
  );
};

const SinglePlaybackDatePicker = ({
  onDateRangeApply,
  onSelectionModeChange,
  selectedDate,
  selectedEndDate,
  selectedEndTime,
  selectedFromTime,
  selectionMode
}: {
  onDateRangeApply: (
    range: DateRange,
    fromTime: string,
    endTime: string
  ) => void;
  onSelectionModeChange: (mode: PlaybackSelectionMode) => void;
  selectedDate: Date;
  selectedEndDate: Date;
  selectedEndTime: string;
  selectedFromTime: string;
  selectionMode: PlaybackSelectionMode;
}) => {
  const [draftDateRange, setDraftDateRange] = useState<DateRange>({
    from: selectedDate,
    to: selectedEndDate
  });
  const [draftFromTime, setDraftFromTime] = useState(selectedFromTime);
  const [draftEndTime, setDraftEndTime] = useState(selectedEndTime);
  const isMultiVehicleMode = selectionMode === "multiVehicle";
  const draftFromDate = normalizePlaybackDate(
    draftDateRange.from ?? selectedDate
  );
  const draftEndDate = normalizePlaybackDate(
    draftDateRange.to ?? draftFromDate
  );
  const isRangeDirty =
    draftFromDate.getTime() !== selectedDate.getTime() ||
    draftEndDate.getTime() !== selectedEndDate.getTime() ||
    draftFromTime !== selectedFromTime ||
    draftEndTime !== selectedEndTime;

  useEffect(() => {
    setDraftDateRange({from: selectedDate, to: selectedEndDate});
    setDraftFromTime(selectedFromTime);
    setDraftEndTime(selectedEndTime);
  }, [selectedDate, selectedEndDate, selectedEndTime, selectedFromTime]);

  const handleDatePickerChange = (
    date: DateRange | Date | Date[] | null
  ): void => {
    if (date instanceof Date && !Number.isNaN(date.getTime())) {
      const nextDate = normalizePlaybackDate(date);
      setDraftDateRange({from: nextDate, to: nextDate});
      return;
    }

    if (date && "from" in date && date.from) {
      const nextFrom = normalizePlaybackDate(date.from);
      const nextTo = normalizePlaybackDate(date.to ?? nextFrom);
      setDraftDateRange({from: nextFrom, to: nextTo});
    }
  };

  const handleFromTimeChange = (time: string) => {
    if (!time) return;
    setDraftFromTime(time);
    setDraftEndTime(currentEndTime =>
      normalizeEndTimeValue(time, currentEndTime)
    );
  };

  const handleEndTimeChange = (time: string) => {
    setDraftEndTime(normalizeEndTimeValue(draftFromTime, time));
  };

  const handleApply = () => {
    onDateRangeApply(
      {
        from: draftFromDate,
        to: isMultiVehicleMode ? draftFromDate : draftEndDate
      },
      draftFromTime,
      draftEndTime
    );
  };

  const toggleSelectionMode = () => {
    onSelectionModeChange(
      isMultiVehicleMode ? "singleVehicle" : "multiVehicle"
    );
  };

  return (
    <div className="grid w-full grid-cols-[124px_minmax(220px,1fr)_176px_176px_96px] gap-2">
      <Tooltip
        content={
          isMultiVehicleMode
            ? "Multi vehicle: one date, all vehicles."
            : "Single vehicle: one vehicle, date range."
        }
        placement="top"
      >
        <button
          type="button"
          aria-pressed={isMultiVehicleMode}
          aria-label={`Switch to ${isMultiVehicleMode ? "single" : "multi"} vehicle mode`}
          onClick={toggleSelectionMode}
          className="flex h-10 min-w-0 items-center justify-center gap-2 rounded-lg border border-[#e5eaf3] bg-white px-3 transition hover:border-[#ffb51f] hover:bg-[#fff7e0]"
        >
          <span
            className={`text-[11px] font-bold transition-colors ${
              !isMultiVehicleMode ? "text-[#f59e0b]" : "text-[#94a3b8]"
            }`}
          >
            Single
          </span>
          <span
            className="relative flex h-5 w-9 flex-none items-center rounded-full transition-colors duration-200"
            style={{
              backgroundColor: isMultiVehicleMode ? "#f59e0b" : "#cbd5e1"
            }}
          >
            <span
              className="absolute h-4 w-4 rounded-full bg-white shadow-sm transition-all duration-200"
              style={{left: isMultiVehicleMode ? "18px" : "2px"}}
            />
          </span>
          <span
            className={`text-[11px] font-bold transition-colors ${
              isMultiVehicleMode ? "text-[#f59e0b]" : "text-[#94a3b8]"
            }`}
          >
            Multi
          </span>
        </button>
      </Tooltip>
      {isMultiVehicleMode ? (
        <NeoDatePicker
          aria-label="Playback date"
          className="w-full"
          inputClassName="!h-10 !min-h-10 !rounded-lg !border !border-[#e5eaf3] !bg-white !text-sm !font-semibold !text-[#30384e] focus:!border-[#ffb51f]"
          maxDate={new Date()}
          type="input"
          value={draftFromDate}
          handleDateChange={handleDatePickerChange}
        />
      ) : (
        <NeoDatePicker
          key={`${selectedDate.getTime()}-${selectedEndDate.getTime()}-${selectionMode}`}
          aria-label="Playback date range"
          className="w-full"
          maxDate={new Date()}
          type="range"
          rangedefaultValue={{from: draftFromDate, to: draftEndDate}}
          handleDateChange={handleDatePickerChange}
        />
      )}
      <label className="flex h-10 min-w-0 overflow-hidden rounded-lg border border-[#e5eaf3] bg-white text-sm font-semibold text-[#30384e] transition focus-within:border-[#5b3fe4]">
        <span className="flex w-14 flex-none items-center border-r border-[#eef2f7] px-2 text-xs font-bold text-[#8390a5]">
          From
        </span>
        <input
          aria-label="Playback from time"
          type="time"
          value={draftFromTime}
          onChange={event => handleFromTimeChange(event.target.value)}
          className="min-w-0 flex-1 bg-transparent px-3 text-sm font-semibold text-[#30384e] outline-none"
        />
      </label>
      <label className="flex h-10 min-w-0 overflow-hidden rounded-lg border border-[#e5eaf3] bg-white text-sm font-semibold text-[#30384e] transition focus-within:border-[#5b3fe4]">
        <span className="flex w-14 flex-none items-center border-r border-[#eef2f7] px-2 text-xs font-bold text-[#8390a5]">
          To
        </span>
        <input
          aria-label="Playback end time"
          type="time"
          min={draftFromTime}
          value={draftEndTime}
          onChange={event => handleEndTimeChange(event.target.value)}
          className="min-w-0 flex-1 bg-transparent px-3 text-sm font-semibold text-[#30384e] outline-none"
        />
      </label>
      <button
        type="button"
        disabled={!isRangeDirty}
        onClick={handleApply}
        className="h-10 rounded-lg bg-neo-black px-3 text-sm font-bold text-white transition  disabled:cursor-not-allowed disabled:bg-[#c8cfda]"
      >
        Apply
      </button>
    </div>
  );
};

const VehiclePanel = ({
  allFilteredSelected,
  canSelectMultipleVehicles,
  currentPlaybackIndex,
  dateRange,
  errorMessage,
  filters,
  isLoading,
  onFilterChange,
  onToggleAll,
  onToggleVehicle,
  onVehicleHover,
  selectedVehicleIds,
  vehicles
}: {
  allFilteredSelected: boolean;
  canSelectMultipleVehicles: boolean;
  currentPlaybackIndex: number;
  dateRange: DateRange;
  errorMessage: string;
  filters: PlaybackFilters;
  isLoading: boolean;
  onFilterChange: <TKey extends keyof PlaybackFilters>(
    key: TKey,
    value: PlaybackFilters[TKey]
  ) => void;
  onToggleAll: () => void;
  onToggleVehicle: (vehicleId: string) => void;
  onVehicleHover: (vehicleId: string | null) => void;
  selectedVehicleIds: Set<string>;
  vehicles: PlaybackVehicle[];
}) => {
  const [sortConfig, setSortConfig] = useState<{
    key: VehicleSortKey;
    direction: SortDirection;
  }>({key: "vehicle", direction: "asc"});

  const sortedVehicles = useMemo(() => {
    return [...vehicles].sort((first, second) => {
      let sortValue = 0;

      if (sortConfig.key === "vehicle") {
        sortValue = first.name.localeCompare(second.name, undefined, {
          numeric: true,
          sensitivity: "base"
        });
      }

      if (sortConfig.key === "km") {
        sortValue =
          getVehicleDistanceValue(first) - getVehicleDistanceValue(second);
      }

      if (sortConfig.key === "time") {
        sortValue = getVehicleTimeValue(first) - getVehicleTimeValue(second);
      }

      return sortConfig.direction === "asc" ? sortValue : -sortValue;
    });
  }, [sortConfig, vehicles]);

  const updateSort = (key: VehicleSortKey) => {
    setSortConfig(current => ({
      key,
      direction:
        current.key === key && current.direction === "asc" ? "desc" : "asc"
    }));
  };

  return (
    <aside className="flex min-h-0 flex-col border-r border-[#e8edf5] bg-[#f6f6f6]">
      <div className="border-b border-[#e4e7ec] bg-white px-3 py-3">
        <div className="flex items-center gap-3">
          <label className="relative block min-w-0 flex-1">
            <FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#4b5563]" />
            <input
              value={filters.search}
              onChange={event => onFilterChange("search", event.target.value)}
              placeholder="Search"
              className="h-10 w-full rounded-2xl border border-[#d7dbe2] bg-white pl-9 pr-3 text-sm font-medium text-[#1f2937] outline-none transition placeholder:text-[#6b7280] focus:border-[#ffb51f] focus:ring-2 focus:ring-[#ffb51f]/20"
            />
          </label>
          {/* <button
            type="button"
            aria-label="Filter vehicles"
            className="flex h-10 w-10 flex-none items-center justify-center rounded-lg text-[#4b5563] transition hover:bg-[#fff7e0] hover:text-[#f59e0b]"
          >
            <FiFilter className="h-6 w-6" />
          </button> */}
        </div>
      </div>

      <div className="flex items-center justify-between border-b border-[#e4e7ec] bg-white px-3 py-3">
        {canSelectMultipleVehicles ? (
          <label className="flex items-center gap-3 text-sm font-semibold text-[#1f2937]">
            <input
              type="checkbox"
              checked={allFilteredSelected}
              onChange={onToggleAll}
              className="h-4 w-4 accent-[#ffb51f]"
            />
            Select All
          </label>
        ) : (
          <span className="text-sm font-semibold text-[#1f2937]">
            Single Vehicle
          </span>
        )}
        <div className="flex items-center gap-1">
          <VehicleSortButton
            activeSort={sortConfig}
            label="Vehicle"
            onSort={updateSort}
            sortKey="vehicle"
          />
          <VehicleSortButton
            activeSort={sortConfig}
            label="Km"
            onSort={updateSort}
            sortKey="km"
          />
          <VehicleSortButton
            activeSort={sortConfig}
            label="Time"
            onSort={updateSort}
            sortKey="time"
          />
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden p-3">
        {isLoading ? (
          <div className="flex min-h-32 items-center justify-center">
            <Spinner size="sm" />
          </div>
        ) : errorMessage ? (
          <EmptyState title={errorMessage} />
        ) : vehicles.length ? (
          <div className="space-y-3">
            {sortedVehicles.map(vehicle => (
              <VehicleCardRow
                key={vehicle.id}
                currentPlaybackIndex={currentPlaybackIndex}
                dateRange={dateRange}
                isSelected={selectedVehicleIds.has(vehicle.id)}
                onHover={onVehicleHover}
                onToggle={() => onToggleVehicle(vehicle.id)}
                vehicle={vehicle}
              />
            ))}
          </div>
        ) : (
          <EmptyState title="No vehicles match the filters" />
        )}
      </div>
    </aside>
  );
};

const VehicleSortButton = ({
  activeSort,
  label,
  onSort,
  sortKey
}: {
  activeSort: {key: VehicleSortKey; direction: SortDirection};
  label: string;
  onSort: (key: VehicleSortKey) => void;
  sortKey: VehicleSortKey;
}) => {
  const isActive = activeSort.key === sortKey;
  const nextDirection =
    isActive && activeSort.direction === "asc" ? "desc" : "asc";
  const SortIcon =
    isActive && activeSort.direction === "desc" ? FiArrowDown : FiArrowUp;

  return (
    <button
      type="button"
      aria-label={`Sort ${label} ${nextDirection}`}
      onClick={() => onSort(sortKey)}
      className={`flex h-7 items-center gap-1 rounded-md px-2 text-xs font-bold transition ${
        isActive
          ? "bg-[#fff2cc] text-[#c77a00]"
          : "text-[#667085] hover:bg-[#fff7e0] hover:text-[#c77a00]"
      }`}
    >
      <span>{label}</span>
      <SortIcon className="h-3 w-3" />
    </button>
  );
};

const VehicleCardRow = ({
  currentPlaybackIndex,
  dateRange,
  isSelected,
  onHover,
  onToggle,
  vehicle
}: {
  currentPlaybackIndex: number;
  dateRange: DateRange;
  isSelected: boolean;
  onHover: (vehicleId: string | null) => void;
  onToggle: () => void;
  vehicle: PlaybackVehicle;
}) => {
  const playbackMetrics = isSelected
    ? getPlaybackSidebarMetrics(vehicle, dateRange, currentPlaybackIndex)
    : null;
  const speed = Math.round(playbackMetrics?.speed ?? 0);
  const updatedPlaybackKm = playbackMetrics?.distanceKm ?? 0;
  const timestamp = playbackMetrics?.timestamp
    ? formatDateTime(playbackMetrics.timestamp)
    : "No data";

  return (
    <button
      type="button"
      aria-pressed={isSelected}
      onClick={onToggle}
      onMouseEnter={() => onHover(vehicle.id)}
      onMouseLeave={() => onHover(null)}
      className={`w-full rounded-lg border bg-white px-3 py-4 text-left shadow-[0_1px_5px_rgba(16,24,40,0.12)] transition hover:-translate-y-0.5 hover:border-[#ffb51f] hover:shadow-[0_6px_16px_rgba(16,24,40,0.14)] ${
        isSelected ? "border-[#ffb51f] bg-[#fffaf0]" : "border-[#dfe3ea]"
      }`}
    >
      <div className="flex min-w-0 items-center gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-start justify-between gap-3">
            <div className="flex min-w-0 items-center gap-2">
              <span
                className="h-2.5 w-2.5 flex-none rounded-full"
                style={{backgroundColor: vehicle.color}}
              />
              <p
                title={vehicle.name}
                className="truncate text-sm font-bold text-[#111827]"
              >
                {vehicle.name}
              </p>
            </div>
            <span className="min-w-max text-xs font-medium text-[#111827]">
              {timestamp}
            </span>
          </div>

          <div className="mt-3 flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-sm">
            <VehicleMetric className="text-xs font-bold text-[#111827]">
              {/* <FiMapPin className="h-4 w-4 text-[#ffb51f]" /> */}
              <img
                src="/icons/distance.svg"
                className="relative z-10 rounded-none opacity-0 shadow-none shadow-black/5 !duration-300 transition-transform-opacity data-[loaded=true]:opacity-100 motion-reduce:transition-none"
                alt="Distance"
                data-loaded="true"
              ></img>
              {formatDistance(vehicle.distanceKm)}
            </VehicleMetric>
            <span className="h-4 w-px bg-[#c9cdd4]" />
            <VehicleMetric className="text-xs font-bold text-[#111827]">
              <img
                src="/icons/vehicleSpeed.svg"
                className="relative z-10 rounded-none opacity-0 shadow-none shadow-black/5 !duration-300 transition-transform-opacity data-[loaded=true]:opacity-100 motion-reduce:transition-none"
                alt="speed"
                data-loaded="true"
              ></img>
              {speed} kph
            </VehicleMetric>
            <span className="h-4 w-px bg-[#c9cdd4]" />
            <VehicleMetric className="text-xs font-bold text-[#f59e0b]">
              <Tooltip
                content="Updated KM is calculated from playback distance up to the selected timeline frame."
                placement="top"
              >
                <span
                  aria-label="Updated KM description"
                  className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-[#ffb51f] text-[#f59e0b]"
                >
                  <FiInfo className="h-3 w-3" />
                </span>
              </Tooltip>
              {formatDistance(updatedPlaybackKm)}
            </VehicleMetric>
          </div>

          {showVehicleEventBadges && (
            <div className="mt-2 flex flex-wrap gap-1">
              <EventCountBadge
                label="A"
                value={vehicle.eventCounts.acceleration}
              />
              <EventCountBadge
                label="H"
                value={vehicle.eventCounts.harshBraking}
              />
              <EventCountBadge
                label="O"
                value={vehicle.eventCounts.overSpeed}
              />
            </div>
          )}
        </div>

        <FiChevronRight className="h-6 w-6 flex-none text-[#111827]" />
      </div>
    </button>
  );
};

const VehicleMetric = ({
  children,
  className
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <span className={`inline-flex min-w-0 items-center gap-1 ${className}`}>
      {children}
    </span>
  );
};

const EventCountBadge = ({label, value}: {label: string; value: number}) => {
  return (
    <span className="min-w-0 rounded-md bg-[#fff2cc] px-2 py-1 text-center text-[11px] font-bold text-[#c77a00]">
      {label} {value}
    </span>
  );
};

const MapPlaybackCanvas = ({
  activeRouteGroups,
  currentMarkers,
  currentPlaybackIndex,
  hoveredVehicleId,
  isPlaying,
  isRouteHistoryLoading,
  isSingleVehicle,
  maxPlaybackIndex,
  onReset,
  onSeek,
  onSpeedDown,
  onSpeedUp,
  onTogglePlay,
  playSpeed,
  selectedVehicleCount
}: {
  activeRouteGroups: VehicleRouteGroup[];
  currentMarkers: PlaybackMarker[];
  currentPlaybackIndex: number;
  hoveredVehicleId: string | null;
  isPlaying: boolean;
  isRouteHistoryLoading: boolean;
  isSingleVehicle: boolean;
  maxPlaybackIndex: number;
  onReset: () => void;
  onSeek: (index: number) => void;
  onSpeedDown: () => void;
  onSpeedUp: () => void;
  onTogglePlay: () => void;
  playSpeed: number;
  selectedVehicleCount: number;
}) => {
  const showNoVehicle = !isRouteHistoryLoading && selectedVehicleCount === 0;
  const showNoData =
    !isRouteHistoryLoading &&
    selectedVehicleCount > 0 &&
    activeRouteGroups.length === 0;

  return (
    <section className="relative min-h-0 overflow-hidden bg-[#f8fbff]">
      <PlaybackGoogleMap
        activeRouteGroups={activeRouteGroups}
        currentMarkers={currentMarkers}
        hoveredVehicleId={hoveredVehicleId}
        isSingleVehicle={isSingleVehicle}
      />

      {(showNoVehicle || showNoData) && (
        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3 rounded-xl border border-[#e5eaf3] bg-white/95 px-10 py-7 text-center shadow-[0_4px_24px_rgba(46,58,89,0.10)]">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#fff7e0]">
              {showNoVehicle ? (
                <FiTruck className="h-7 w-7 text-[#f59e0b]" />
              ) : (
                <FiAlertCircle className="h-7 w-7 text-[#f59e0b]" />
              )}
            </span>
            <p className="text-sm font-bold text-[#30384e]">
              {showNoVehicle ? "No Vehicle Selected" : "No History Found"}
            </p>
            <p className="max-w-[220px] text-xs leading-relaxed text-[#8390a5]">
              {showNoVehicle
                ? "Select a vehicle from the left panel to view its playback history."
                : "No route data available for the selected time range. Try adjusting the date or time filters."}
            </p>
          </div>
        </div>
      )}

      {isRouteHistoryLoading && (
        <div className="absolute left-4 top-4 z-20 flex items-center gap-2 rounded-lg border border-[#e5eaf3] bg-white/95 px-3 py-2 text-xs font-bold text-[#667085] shadow-sm">
          <Spinner size="sm" />
          Loading route history
        </div>
      )}
      <PlaybackControlDock
        currentMarkers={currentMarkers}
        currentPlaybackIndex={currentPlaybackIndex}
        isPlaying={isPlaying}
        maxPlaybackIndex={maxPlaybackIndex}
        onReset={onReset}
        onSeek={onSeek}
        onSpeedDown={onSpeedDown}
        onSpeedUp={onSpeedUp}
        onTogglePlay={onTogglePlay}
        playSpeed={playSpeed}
      />
    </section>
  );
};

const PlaybackGoogleMap = ({
  activeRouteGroups,
  currentMarkers,
  hoveredVehicleId,
  isSingleVehicle
}: {
  activeRouteGroups: VehicleRouteGroup[];
  currentMarkers: PlaybackMarker[];
  hoveredVehicleId: string | null;
  isSingleVehicle: boolean;
}) => {
  const {isLoaded, loadError} = useJsApiLoader({googleMapsApiKey: mapKey});
  const {filterContext} = useNeoContext();
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [selectedMarker, setSelectedMarker] = useState<PlaybackMarker | null>(
    null
  );
  const polylineRefs = useRef<Map<string, google.maps.Polyline>>(new Map());
  const defaultCenter = {lat: 11.0168, lng: 76.9558};
  const routeOverlayKey =
    activeRouteGroups.map(getRouteRenderKey).join("|") || "empty-routes";

  useEffect(() => {
    const activeRouteKeys = new Set(activeRouteGroups.map(getRouteRenderKey));

    Array.from(polylineRefs.current.entries()).forEach(([key, polyline]) => {
      if (!activeRouteKeys.has(key)) {
        polyline.setMap(null);
        polylineRefs.current.delete(key);
      }
    });

    if (activeRouteKeys.size === 0) {
      setSelectedMarker(null);
    }
  }, [activeRouteGroups]);

  useEffect(() => {
    if (activeRouteGroups.length === 0) {
      setSelectedMarker(null);
    }

    if (!isLoaded || !map || activeRouteGroups.length === 0) return;

    const points = activeRouteGroups.flatMap(group => group.route);
    if (points.length === 1) {
      map.setCenter({lat: points[0].lat, lng: points[0].lng});
      map.setZoom(15);
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    points.forEach(point => bounds.extend({lat: point.lat, lng: point.lng}));
    map.fitBounds(bounds);
  }, [activeRouteGroups, isLoaded, map]);

  useEffect(() => {
    if (!selectedMarker) return;

    const current = currentMarkers.find(
      marker => marker.vehicle.id === selectedMarker.vehicle.id
    );
    setSelectedMarker(current ?? null);
  }, [currentMarkers, selectedMarker]);

  useEffect(() => {
    if (!hoveredVehicleId || !map) return;
    const marker = currentMarkers.find(m => m.vehicle.id === hoveredVehicleId);
    if (marker) {
      map.panTo({lat: marker.point.lat, lng: marker.point.lng});
    }
  }, [hoveredVehicleId, currentMarkers, map]);

  if (loadError) {
    return <NeoError message={loadError.message} />;
  }

  if (!isLoaded) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <GoogleMap
      key={routeOverlayKey}
      mapContainerStyle={{width: "100%", height: "100%"}}
      center={map ? undefined : defaultCenter}
      zoom={13}
      onLoad={setMap}
      onUnmount={() => setMap(null)}
      options={{
        styles: mapStyles,
        fullscreenControl: true,
        mapTypeControl: true,
        streetViewControl: false
      }}
    >
      {filterContext?.client?.insLat !== undefined &&
        filterContext?.client?.insLng !== undefined && (
          <Marker
            position={{
              lat: filterContext.client.insLat,
              lng: filterContext.client.insLng
            }}
            title={filterContext.client.institutionName || "School"}
            icon={{
              url: "/images/school_3.png",
              scaledSize: new google.maps.Size(40, 40)
            }}
          />
        )}
      {activeRouteGroups.map(group => (
        <Polyline
          key={getRouteRenderKey(group)}
          onLoad={polyline => {
            polylineRefs.current.set(getRouteRenderKey(group), polyline);
          }}
          onUnmount={polyline => {
            polyline.setMap(null);
            polylineRefs.current.delete(getRouteRenderKey(group));
          }}
          path={group.route.map(point => ({lat: point.lat, lng: point.lng}))}
          options={{
            strokeColor: group.vehicle.color,
            strokeOpacity: 0.9,
            strokeWeight: 4
          }}
        />
      ))}

      {currentMarkers.map((marker, index) => (
        <Marker
          key={marker.vehicle.id}
          icon={{
            url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
              BusIcon.replace("%color%", marker.vehicle.color)
            )}`,
            scaledSize: new window.google.maps.Size(40, 40)
          }}
          label={
            isSingleVehicle
              ? undefined
              : {
                  text: getVehicleLabel(marker.vehicle.name, index),
                  color: "#000000",
                  fontSize: "10px",
                  fontWeight: "bold"
                }
          }
          position={{lat: marker.point.lat, lng: marker.point.lng}}
          title={marker.vehicle.name}
          onClick={() => setSelectedMarker(marker)}
        />
      ))}

      {selectedMarker && (
        <InfoWindow
          position={{
            lat: selectedMarker.point.lat,
            lng: selectedMarker.point.lng
          }}
          onCloseClick={() => setSelectedMarker(null)}
        >
          <div className="min-w-[190px] text-[#30384e]">
            <p className="font-bold">{selectedMarker.vehicle.name}</p>
            <p className="mt-1 text-xs font-semibold text-[#667085]">
              {selectedMarker.vehicle.routeName}
            </p>
            <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
              <span className="font-semibold text-[#8390a5]">Time</span>
              <span className="font-bold">
                {formatTime(selectedMarker.point.timestamp)}
              </span>
              <span className="font-semibold text-[#8390a5]">Speed</span>
              <span className="font-bold">
                {selectedMarker.point.speed} kph
              </span>
              {/* <span className="font-semibold text-[#8390a5]">Event</span>
              <span className="font-bold">{selectedMarker.point.event}</span> */}
              <span className="font-semibold text-[#8390a5]">Distance</span>
              <span className="font-bold">
                {formatDistance(selectedMarker.point.distanceKm)}
              </span>
            </div>
          </div>
        </InfoWindow>
      )}
    </GoogleMap>
  );
};

const PlaybackControlDock = ({
  currentMarkers,
  currentPlaybackIndex,
  isPlaying,
  maxPlaybackIndex,
  onReset,
  onSeek,
  onSpeedDown,
  onSpeedUp,
  onTogglePlay,
  playSpeed
}: {
  currentMarkers: PlaybackMarker[];
  currentPlaybackIndex: number;
  isPlaying: boolean;
  maxPlaybackIndex: number;
  onReset: () => void;
  onSeek: (index: number) => void;
  onSpeedDown: () => void;
  onSpeedUp: () => void;
  onTogglePlay: () => void;
  playSpeed: number;
}) => {
  const timelineSummary = getPlaybackTimelineSummary(currentMarkers);
  const timelineProgress =
    maxPlaybackIndex > 0 ? (currentPlaybackIndex / maxPlaybackIndex) * 100 : 0;

  return (
    <div className="absolute bottom-6 left-8 right-8 z-20 rounded-lg border border-[#e5eaf3] bg-white/95 p-4 shadow-[0_18px_45px_rgba(46,58,89,0.14)] backdrop-blur">
      <div className="flex items-center gap-4">
        <button
          aria-label={isPlaying ? "Pause playback" : "Start playback"}
          disabled={maxPlaybackIndex === 0}
          onClick={onTogglePlay}
          className="flex h-12 w-12 flex-none items-center justify-center rounded-lg bg-neo-black text-white shadow-[0_10px_24px_rgba(0,0,0,0.22)] transition hover:bg-black disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isPlaying ? (
            <FiPause className="h-6 w-6 fill-current" />
          ) : (
            <FiPlay className="ml-0.5 h-6 w-6 fill-current" />
          )}
        </button>

        <div className="min-w-0 flex-1">
          <div className="mb-2 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <h3 className="truncate text-sm font-bold text-[#30384e]">
                Fleet Replay Timeline
              </h3>
              <p className="truncate text-xs font-semibold text-[#8390a5]">
                {timelineSummary}
              </p>
            </div>
            <span className="min-w-max text-xs font-bold text-[#667085]">
              Frame {Math.min(currentPlaybackIndex + 1, maxPlaybackIndex + 1)} /{" "}
              {maxPlaybackIndex + 1}
            </span>
          </div>
          <input
            aria-label="Playback timeline"
            type="range"
            min={0}
            max={maxPlaybackIndex}
            value={currentPlaybackIndex}
            onChange={event => onSeek(Number(event.target.value))}
            style={{
              background: `linear-gradient(to right, #ffb51f 0%, #ffb51f ${timelineProgress}%, #8a8a8a ${timelineProgress}%, #8a8a8a 100%)`
            }}
            className="h-1.5 w-full cursor-pointer appearance-none rounded-full [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-[#ffb51f] [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:shadow-[0_1px_4px_rgba(0,0,0,0.25)] [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-[#ffb51f] [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:shadow-[0_1px_4px_rgba(0,0,0,0.25)]"
          />
        </div>

        <div className="flex h-11 items-center overflow-hidden rounded-lg border border-[#e5eaf3] bg-white shadow-sm">
          <button
            aria-label="Decrease playback speed"
            onClick={onSpeedDown}
            className="flex h-full w-11 items-center justify-center text-[#2f3750] transition hover:bg-[#f5f7fb]"
          >
            <FiMinus className="h-4 w-4" />
          </button>
          <span className="flex h-full min-w-14 items-center justify-center border-x border-[#eef2f7] text-sm font-bold">
            {playSpeed}x
          </span>
          <button
            aria-label="Increase playback speed"
            onClick={onSpeedUp}
            className="flex h-full w-11 items-center justify-center text-[#2f3750] transition hover:bg-[#f5f7fb]"
          >
            <FiPlus className="h-4 w-4" />
          </button>
        </div>

        <button
          aria-label="Reset playback"
          onClick={onReset}
          className="flex h-11 w-11 flex-none items-center justify-center rounded-lg border border-[#e5eaf3] bg-white text-[#566176] shadow-sm transition hover:border-[#cfd8ec]"
        >
          <FiRefreshCw className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

const MiniMetric = ({
  icon,
  label,
  value
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) => {
  return (
    <div className="flex min-w-0 items-center gap-2 rounded-lg bg-[#f7f9fd] px-3 py-2">
      <span className="text-[#5b3fe4] [&_svg]:h-4 [&_svg]:w-4">{icon}</span>
      <span className="text-[#98a2b3]">{label}</span>
      <span className="min-w-0 truncate font-bold text-[#30384e]">{value}</span>
    </div>
  );
};

const RightStack = ({
  currentPlaybackIndex,
  eventCounts,
  onPageChange,
  onRowClick,
  page,
  pagedRows,
  recordCount,
  tableRows,
  totalPages
}: {
  currentPlaybackIndex: number;
  eventCounts: Array<{label: PlaybackEvent; count: number}>;
  onPageChange: (page: number) => void;
  onRowClick: (row: PlaybackTableRow) => void;
  page: number;
  pagedRows: PlaybackTableRow[];
  recordCount: number;
  tableRows: PlaybackTableRow[];
  totalPages: number;
}) => {
  return (
    <aside className="flex min-h-0 flex-col gap-4 overflow-hidden border-l border-[#e8edf5] bg-white px-3 py-4">
      <section className="rounded-lg border border-[#e5eaf3] bg-white p-4 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <p className="text-sm font-semibold">Event Counts</p>
          <span className="text-xs font-bold text-[#98a2b3]">
            {recordCount} records
          </span>
        </div>

        <div className="grid grid-cols-2 gap-2">
          {eventCounts.map(event => (
            <article
              key={event.label}
              className="rounded-lg border border-[#e7edf5] bg-white p-3 shadow-sm"
            >
              <p className="truncate text-xs font-bold text-[#667085]">
                {event.label}
              </p>
              <p className="mt-2 text-2xl font-bold leading-none text-[#23283b]">
                {event.count}
              </p>
            </article>
          ))}
        </div>
      </section>

      <section className="flex min-h-0 flex-1 flex-col rounded-lg border border-[#e5eaf3] bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-[#eef2f7] px-4 py-3">
          <div>
            <h2 className="text-sm font-bold text-[#30384e]">Playback Data</h2>
            <p className="text-xs font-semibold text-[#98a2b3]">
              Click a row to seek the timeline
            </p>
          </div>
          <FiList className="h-5 w-5 text-[#8b95a7]" />
        </div>

        <div className="min-h-0 flex-1 overflow-auto">
          <table className="min-w-full border-separate border-spacing-0 text-left text-xs">
            <thead className="sticky top-0 z-10 bg-[#f8fafd] text-[#667085]">
              <tr>
                <TableHead>Time</TableHead>
                <TableHead>Vehicle</TableHead>
                <TableHead>Speed</TableHead>
                <TableHead>Event</TableHead>
              </tr>
            </thead>
            <tbody>
              {pagedRows.length ? (
                pagedRows.map(row => (
                  <tr
                    key={row.id}
                    onClick={() => onRowClick(row)}
                    className={`cursor-pointer transition hover:bg-[#f6f8fc] ${
                      row.pointIndex === currentPlaybackIndex
                        ? "bg-[#f1f4ff]"
                        : "bg-white"
                    }`}
                  >
                    <TableCell>{formatTime(row.point.timestamp)}</TableCell>
                    <TableCell>
                      <div className="flex min-w-0 items-center gap-2">
                        <span
                          className="h-2.5 w-2.5 flex-none rounded-full"
                          style={{backgroundColor: row.vehicle.color}}
                        />
                        <span
                          title={row.vehicle.name}
                          className="max-w-[110px] truncate font-bold text-[#30384e]"
                        >
                          {row.vehicle.name}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>{row.point.speed} kph</TableCell>
                    <TableCell>
                      <span
                        className={`rounded-md px-2 py-1 font-bold ${eventStyles[row.point.event]}`}
                      >
                        {row.point.event}
                      </span>
                    </TableCell>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4}>
                    <EmptyState title="No playback records" />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between border-t border-[#eef2f7] px-4 py-3">
          <span className="text-xs font-semibold text-[#8390a5]">
            Page {page} of {totalPages}
          </span>
          <div className="flex items-center gap-2">
            <PageButton
              disabled={page === 1}
              onClick={() => onPageChange(Math.max(1, page - 1))}
            >
              <FiChevronLeft className="h-4 w-4" />
            </PageButton>
            <PageButton
              disabled={page === totalPages}
              onClick={() => onPageChange(Math.min(totalPages, page + 1))}
            >
              <FiChevronRight className="h-4 w-4" />
            </PageButton>
          </div>
        </div>
      </section>
    </aside>
  );
};

const TableHead = ({children}: {children: React.ReactNode}) => {
  return (
    <th className="border-b border-[#eef2f7] px-3 py-3 font-bold">
      {children}
    </th>
  );
};

const TableCell = ({children}: {children: React.ReactNode}) => {
  return (
    <td className="border-b border-[#eef2f7] px-3 py-3 align-middle font-semibold text-[#667085]">
      {children}
    </td>
  );
};

const PageButton = ({
  children,
  disabled,
  onClick
}: {
  children: React.ReactNode;
  disabled: boolean;
  onClick: () => void;
}) => {
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      className="flex h-9 min-w-9 items-center justify-center rounded-lg border border-[#e5eaf3] bg-white px-3 text-sm font-bold text-[#566176] disabled:cursor-not-allowed disabled:opacity-45"
    >
      {children}
    </button>
  );
};

const getVehicleLabel = (name: string, index: number) => {
  const parts = name.split("_");
  return parts[0].length <= 3 ? parts[0] : String(index + 1);
};

const EmptyState = ({title}: {title: string}) => {
  return (
    <div className="flex min-h-24 items-center justify-center px-4 py-6 text-center text-sm font-semibold text-[#98a2b3]">
      {title}
    </div>
  );
};
